import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  Image,
  Alert,
  TextInput,
  Modal
} from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from '../../../../hooks/useApiWithLoading';
import { postFeeLookup } from "../../../../services/common.service";
import { ProductType } from "../../../../view-models/productType";
// import { useGlobalError } from '../../../uiComponents/GlobalErrorProvider';
import { useGlobalError }  from '../../../uiComponents/GlobalErrorProvider';

// Icons
import BackIcon from "../../../../../../../assets/images/back_icon.png";
import HelpIcon from "../../../../../../../assets/images/Icon_help.png";
import CloseIcon from "../../../../../../../assets/images/Icon_close.png";
import DownArrow from "../../../../../../../assets/images/DownArrow.png";
import AccountLockedIcon from "../../../../../../../assets/images/accountLocked.png";
import { getFieldsForProductRequest, updateConsumerProfile, validateConsumer, postTransactionToKiosk, postAuthConsumer } from "../../../../services/send.service";
import { buildSendValidationRequestFromStore } from "../../../../helper/sendValidationHelper";
import { buildTransactionParametersFromStore } from "../../../../helper/transactionParameterHelper";


// Send flow step components
// import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep";
import EnterPhoneStep, { EnterPhoneStepHandle } from "./steps/EnterPhoneStep"
import PinInputStep from "./steps/PinInputStep";
import ConfirmPinStep from "./steps/ConfirmPinStep";
import ChooseCountryStep from "./steps/ChooseCountryStep";
import ChooseStateStep from "./steps/ChooseStateStep";
import HowTheyReceiveStep from "./steps/HowTheyReceiveStep";
import SendingAmountEntryStep from "./steps/SendAmountEntryStep";
import ReviewAndEditStep from "./steps/ReviewAndEditStep";
import EnterSenderNameStep from "./steps/EnterSenderNameStep";
import SenderAddressStep from "./steps/SenderAddressStep";
import SenderDateOfBirthStep from "./steps/SenderDateOfBirthStep";
import SenderIdentificationTypeStep from "./steps/SenderIdentificationTypeStep";
import ChooseStateOfIdStep from "./steps/ChooseStateOfIdStep";
import SenderIdentificationNumber from "./steps/SenderIdentificationNumber";
import ReceiverFullNameStep from "./steps/ReceiverFullNameStep";
import FraudPreventionStep from "./steps/FraudPreventionStep";
import ReviewFinalStep from "./steps/ReviewFinalStep";
import ConfirmationStep from "../../common/steps/ConfirmationStep";
import HeadToCashierStep from "./steps/HeadToCashierStep";
import AdditionalInformationStep from "./steps/AdditionalInformationStep";
import { GffpHelper } from "../../../../helper/gffpHelper";
import { FieldsForProductInfo } from "../../../../view-models/FieldsForProductInfo";
import { logUpdateConsumerProfileDraft } from "../../../../helper/createProfileHelper";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../../../../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { buildAuthConsumerPostParameter } from "../../../../helper/authConsumerHelper";
import { AuthConsumerInfo } from "../../../../view-models/AuthConsumerInfo";
import CustomerReceiveNumber from "./steps/CustomerReceiveNumber";
import { RequiredFields } from "../../../../view-models/RequiredFields";
import  Colors  from "../../../../constants/colors";

interface MainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
}

const BASE_STEPS_NEW = [
  { key: "phone", component: EnterPhoneStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "customerReceiveNumber", component: CustomerReceiveNumber },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStep },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  // ⬇️ PIN steps come **after** final review
  { key: "pin", component: PinInputStep },
  { key: "confirmPin", component: ConfirmPinStep },
  { key: "pinCreated", component: ConfirmationStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

const BASE_STEPS_EXISTING = [
  { key: "phone", component: EnterPhoneStep },
  // ⬇️ PIN immediately after phone
  { key: "pin", component: PinInputStep },
  { key: "country", component: ChooseCountryStep },
  { key: "state", component: ChooseStateStep },
  { key: "howTheyReceive", component: HowTheyReceiveStep },
  { key: "sendingAmountEntry", component: SendingAmountEntryStep },
  { key: "reviewAndEditStep", component: ReviewAndEditStep },
  { key: "customerReceiveNumber", component: CustomerReceiveNumber },
  { key: "senderName", component: EnterSenderNameStep },
  { key: "senderAddress", component: SenderAddressStep },
  { key: "senderDOB", component: SenderDateOfBirthStep },
  { key: "senderIdentification", component: SenderIdentificationTypeStep },
  { key: "stateOfId", component: ChooseStateOfIdStep },
  { key: "senderIdNumber", component: SenderIdentificationNumber },
  { key: "receiverName", component: ReceiverFullNameStep },
  { key: "additionalInformation", component: AdditionalInformationStep },
  { key: "fraudPrevention", component: FraudPreventionStep },
  { key: "finalReview", component: ReviewFinalStep },
  { key: "headToCashier", component: HeadToCashierStep },
];

// const steps = [
//   { key: "phone", title: "Enter phone number", component: EnterPhoneStep },
//   { key: "pin", title: "Set PIN", component: PinInputStep },
//   { key: "confirmPin", title: "Confirm your PIN", component: ConfirmPinStep },
//   { key: "pinCreated", title: "PIN Created", component: ConfirmationStep },
//   { key: "country", title: "Choose a country", component: ChooseCountryStep },
//   { key: "state", title: "Choose a state", component: ChooseStateStep },
//   { key: "howTheyReceive", title: "Choose a receiver", component: HowTheyReceiveStep },
//   { key: "sendingAmountEntry", title: "", component: SendingAmountEntryStep },
//   { key: "reviewAndEditStep", title: "Review", component: ReviewAndEditStep },
//   { key: "customerReceiveNumber", title: "", component: CustomerReceiveNumber },
//   { key: "senderName", title: "Your Name", component: EnterSenderNameStep },
//   { key: "senderAddress", title: "Your Address", component: SenderAddressStep },
//   { key: "senderDOB", title: "Your Date of Birth", component: SenderDateOfBirthStep },
//   { key: "senderIdentification", title: "Identification type", component: SenderIdentificationTypeStep },
//   { key: "stateOfId", title: "Choose State of ID", component: ChooseStateOfIdStep },
//   { key: "senderIdNumber", title: "Your identification number", component: SenderIdentificationNumber },
//   { key: "receiverName", title: "Your Receiver name", component: ReceiverFullNameStep },
//   { key: "fraudPrevention", title: "Fraud Prevention", component: FraudPreventionStep },
//   { key: "finalReview", title: "Review", component: ReviewFinalStep },
//   { key: "headToCashier", title: "Head to cashier", component: HeadToCashierStep },
// ];

export const MainSendCashScreen: React.FC<MainSendScreenProps> = ({
  handleClose,
  handleFinish
}) => {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();
  const [currentStep, setCurrentStep] = useState(0);

  const [steps, setSteps] = useState(BASE_STEPS_EXISTING); // temporary
  // const [currentStep, setCurrentStep] = useState(0);

  const [returnToStep, setReturnToStep] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const isSendCash = useSessionStore((s: SessionState) => (s as any).isSendCash);
  const isReceiver = useSessionStore((s: SessionState) => (s as any).isReceiver);
  console.log('isSendCash==>', isSendCash, "isReceiver==>", isReceiver)
  const phoneStepRef = React.useRef<EnterPhoneStepHandle | null>(null);
  // Pin Verification
  // const [pin, setPin] = useState(["", "", "", ""]);
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [locked, setLocked] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const inputs = useRef<(TextInput | null)[]>([]);
  const setConsumerPin = useSessionStore((s: any) => s.setConsumerPin);

  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOptionValue = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const setLastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).setLastFeeLookupResponse);
  const setGffpCategories = useSessionStore((s: SessionState) => (s as any).setGffpCategories);
  const setGffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).setGffpFieldsByCategory);
  const setMissingRequiredFields = useSessionStore((s: SessionState) => (s as any).setMissingRequiredFields);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues);
  const selectedIdPhotoIdState = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdState);
  const selectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdCountry);
  const recordStepTransition = useSessionStore((s: SessionState) => (s as any).recordStepTransition);
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  // Sender profile setters to hydrate from AuthConsumer
  const setConsumerId = useSessionStore((s: SessionState) => (s as any).setConsumerId);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);
  const setPhoneNumber = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  const setConfirmationNumber = useSessionStore((s: SessionState) => (s as any).setConfirmationNumber);
  const consumerIdFromStore = useSessionStore((s: SessionState) => (s as any).consumerId);
  const StepComponent = steps[currentStep].component;
  const AnyStepComponent = StepComponent as any;
  const [rrnFieldLabel, setRrnFieldLabel] = useState<string | undefined>(undefined);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);


  // initialize timing for first step
  useEffect(() => {
    try { recordStepTransition?.(steps[0].key); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback each step can use to report validity
  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleEditStep = (key: string) => {
    console.log('handleEditStep=>',key)
     if (key === 'country') {
      console.log('CountryhandleEditStep=>',key)
      // ✅ Go to country step, but DO NOT set returnToStep.
      const targetIndex = steps.findIndex(s => s.key === 'country');
      if (targetIndex !== -1) setCurrentStep(targetIndex);
    } else {
      const targetIndex = steps.findIndex(s => s.key === key);
      if (targetIndex !== -1) {
        console.log('setReturnToStep=>',steps[currentStep].key, 'targetIndex==>', targetIndex)
        setReturnToStep(steps[currentStep].key);   // "reviewAndEditStep"✅ remember where we came from
        setCurrentStep(targetIndex); // 7th index
      }}
  };

  const handlePinVerification = async () => {
    // replace with real validation
     try {
          const authConsumerPostParameter = buildAuthConsumerPostParameter();
          console.log("authConsumerPostParameter===", authConsumerPostParameter);
          const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(authConsumerPostParameter);
          console.log("authConsumerResponse===", authConsumerResponse);
          const respAny: any = authConsumerResponse as any;
          if (respAny?.ErrorDetail || respAny?.errorDetail) {
            console.log('Wrong PIN==>', attemptsLeft)
            const remaining = attemptsLeft - 1;
            setAttemptsLeft(remaining);
            setResetSignal(prev => prev + 1);
            setConsumerPin("");
            inputs.current[0]?.focus();
            if (remaining <= 0) {
              setLocked(true);
              setShowPopup(true);
            }
            // try { showError('Incorrect PIN'); } catch {}
            return; // stay on PIN step when auth returns an error detail
          }
          // Hydrate sender profile details into the store from successful AuthConsumer
          const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
          if (ci) {
            // Consumer ID
            try { setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? '')); } catch {}
            try { setSenderFirstName?.(ci.SenderFirstName ?? ci.senderFirstName ?? null); } catch {}
            try { setSenderMiddleName?.(ci.SenderMiddleName ?? ci.senderMiddleName ?? null); } catch {}
            try { setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null); } catch {}
            try { setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null); } catch {}
            try { setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null); } catch {}
            try { setSenderState?.(ci.SenderState ?? ci.senderState ?? null); } catch {}
            try { setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null); } catch {}
            // Phone
            try { setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null); } catch {}
            // DOB
            const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
            if (dobRaw) {
              const dobStr = String(dobRaw).split('T')[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
              try { setSenderDOBString?.(dobStr || null); } catch {}
              const dobDate = new Date(dobStr);
              if (!isNaN(dobDate as any)) {
                try { setSenderDOB?.(dobDate); } catch {}
              }
            }
          }
        } catch {}
        const targetIndex = steps.findIndex(s => s.key === "country");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
  };

  const handleNext = async (countryCode?: string) => {
    console.log('handleNext===>', handleNext);
    // Only EnterPhoneStep triggers the ConsumerLookup
    await executeWithLoading(async () => {
      // Your existing logic here
      console.log('steps[currentStep].key===', steps[currentStep].key)
      // ⬇️ Handle "return to previous step" FIRST
      console.log('returnToStep===>', returnToStep);
      if (returnToStep) {
        console.log('returnToStep===>', returnToStep);
        const targetIndex = steps.findIndex(s => s.key === returnToStep);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
        }
        setReturnToStep(null);   // clear for next time
        return;                  // stop normal forward flow
      }
      // ---- normal forward navigation below ----
      if (steps[currentStep].key === "phone") {
        try {
          const result = await phoneStepRef.current?.submit();
          if (result === 'notfound') {
            // Treat as new consumer
            handleCustomerNotFound();
            // console.log('NOTFOUNT currentStep==>', currentStep)
            // setSteps(BASE_STEPS_NEW);
            // setCurrentStep(1); // move to the country step
            // return;
            setSteps(() => {
              return BASE_STEPS_NEW;
            });
            // advance on the next render so BASE_STEPS_NEW is fixed
            requestAnimationFrame(() => setCurrentStep(
                BASE_STEPS_NEW.findIndex(s => s.key === 'country')
            ));
            return;
          } else {
            // Existing consumer
            setSteps(BASE_STEPS_EXISTING);
            setCurrentStep(1); // move to PinInput
          }
          // Success path: go to PIN step
          const targetIndex = steps.findIndex(s => s.key === "pin");
          if (targetIndex !== -1) {
            setCurrentStep(targetIndex);
            return;
          }
        } catch {
          // submit already surfaced alerts; just stop progression
          return;
        }
      } else if (steps[currentStep].key === "country") {
        // After selecting a country, branch to state or amount entry
        const isUsOrCa = countryCode === 'USA' || countryCode === 'CAN';
        const targetKey = isUsOrCa ? 'state' : 'sendingAmountEntry';
        const targetIndex = steps.findIndex(s => s.key === targetKey);
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "state") {
        // After selecting a country, branch to state or amount entry
        const targetIndex = steps.findIndex(s => s.key === 'sendingAmountEntry');
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "pin") {
        // If this is a new consumer, go to confirm PIN step instead of auth
        if (isNewConsumer) {
          const confirmIndex = steps.findIndex(s => s.key === "confirmPin");
          if (confirmIndex !== -1) {
            try { recordStepTransition?.("confirmPin"); } catch {}
            setCurrentStep(confirmIndex);
            return;
          }
        }
        // Existing consumer: build the auth consumer request and proceed to choose country
        handlePinVerification();
        // try {
        //   const authConsumerPostParameter = buildAuthConsumerPostParameter();
        //   console.log("authConsumerPostParameter===", authConsumerPostParameter);
        //   const authConsumerResponse: AuthConsumerInfo = await postAuthConsumer(authConsumerPostParameter);
        //   console.log("authConsumerResponse===", authConsumerResponse);
        //   const respAny: any = authConsumerResponse as any;
        //   if (respAny?.ErrorDetail || respAny?.errorDetail) {
        //     try { showError('Incorrect PIN'); } catch {}
        //     return; // stay on PIN step when auth returns an error detail
        //   }
        //   // Hydrate sender profile details into the store from successful AuthConsumer
        //   const ci: any = respAny?.ConsumerInfo ?? respAny?.consumerInfo;
        //   if (ci) {
        //     // Consumer ID
        //     try { setConsumerId?.(String(ci.ConsumerId ?? ci.consumerId ?? '')); } catch {}
        //     try { setSenderFirstName?.(ci.SenderFirstName ?? ci.senderFirstName ?? null); } catch {}
        //     try { setSenderMiddleName?.(ci.SenderMiddleName ?? ci.senderMiddleName ?? null); } catch {}
        //     try { setSenderLastName?.(ci.SenderLastName ?? ci.senderLastName ?? null); } catch {}
        //     try { setSenderAddress?.(ci.SenderAddress ?? ci.senderAddress ?? null); } catch {}
        //     try { setSenderCity?.(ci.SenderCity ?? ci.senderCity ?? null); } catch {}
        //     try { setSenderState?.(ci.SenderState ?? ci.senderState ?? null); } catch {}
        //     try { setSenderZipCode?.(ci.SenderZipCode ?? ci.senderZipCode ?? null); } catch {}
        //     // Phone
        //     try { setPhoneNumber?.(ci.SenderHomePhone ?? ci.senderHomePhone ?? null); } catch {}
        //     // DOB
        //     const dobRaw: any = ci.SenderDOB ?? ci.senderDOB ?? null;
        //     if (dobRaw) {
        //       const dobStr = String(dobRaw).split('T')[0]; // handle YYYY-MM-DD or YYYY-MM-DDThh:mm:ss
        //       try { setSenderDOBString?.(dobStr || null); } catch {}
        //       const dobDate = new Date(dobStr);
        //       if (!isNaN(dobDate as any)) {
        //         try { setSenderDOB?.(dobDate); } catch {}
        //       }
        //     }
        //   }
        // } catch {}
        // const targetIndex = steps.findIndex(s => s.key === "country");
        // if (targetIndex !== -1) {
        //   setCurrentStep(targetIndex);
        //   return;
        // }
      } else if (steps[currentStep].key === "confirmPin") {
        // After confirming PIN for a new consumer, go to PIN created or head to cashier path
        // No early redirect to additionalInformation here; we will handle missing fields after receiverName
        const result = await maultipleCalls();
        if (result && (result as any).error) {
          // An error was shown; stay on current step
          return;
        }
        if(isNewConsumer) {
          const targetIndex = steps.findIndex(s => s.key === "pinCreated");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("pinCreated"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
        
        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("headToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "sendingAmountEntry") {
        // Call FeeLookup with updated amount before moving to HowTheyReceive
        if (agentId && unitProfileId && selectedDestinationCountryCode && currentSendAmount) {
          const amountNum = Number(String(currentSendAmount).replace(/,/g, ""));
          if (isFinite(amountNum) && amountNum > 0) {
            const feeResp = await postFeeLookup({
              agentID: agentId,
              unitProfileID: unitProfileId,
              amount: amountNum,
              amountExcludingFee: amountNum,
              sendCurrency: (pendingSendOption?.SendAmounts?.SendCurrency) || 'USD',
              destinationCountry: selectedDestinationCountryCode,
              destinationState: selectedDestinationStateAbbr,
              receiveCurrency: selectedReceiveCurrency || undefined,
              allOptions: true,
              productType: ProductType.SEND,
            });
            // Inspect potential error envelope in fee lookup response
            try {
              const rawData: any = (feeResp && (feeResp as any).data !== undefined) ? (feeResp as any).data : feeResp;
              let parsed: any = rawData;
              if (typeof rawData === 'string') {
                try { parsed = JSON.parse(rawData); } catch {}
              }
              const errDetail = parsed?.ErrorDetail || parsed?.errorDetail;
              if (errDetail) {
                const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? 'UNKNOWN';
                const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? 'An error occurred.';
                console.warn('FeeLookup ErrorDetail:', { errCode, errString });
               // Alert.alert('Message', String(errString), [{ text: 'OK' }]);
                showError(errCode + ' ' + errString);
                return; // stay on current step if fee lookup returned an error
              }
            } catch {}
            try {
             setLastFeeLookupResponse?.(feeResp);
            } catch { }
          }
        }
        const targetIndex = steps.findIndex(s => s.key === "howTheyReceive");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
      /*else if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
        return;
      }*/
      else if (steps[currentStep].key === "howTheyReceive") {
        // Commit the pending option (chosen in HowTheyReceive) before moving forward
        if (pendingSendOption) {
          setSelectedSendOption(pendingSendOption);
        }
        const targetIndex = steps.findIndex(s => s.key === "reviewAndEditStep");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "reviewAndEditStep") {

        // Call GetFieldsForProduct on Continue from Review step
        try {
          const opt: any = pendingSendOption || selectedSendOptionValue || {};
          const amountFromState = Number(String(currentSendAmount || "0").replace(/,/g, ""));
          const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
          const payload: any = {
            unitProfileID: unitProfileId || "",
            agentID: agentId || "",
            receiveCountry: selectedDestinationCountryCode || opt?.DestinationCountry || "",
            deliveryOption: opt?.DeliveryOption || "",
            thirdPartyType: "NONE",
            receiveCurrency: selectedReceiveCurrency || opt?.ValidReceiveCurrency || opt?.EstimatedReceiveCurrency || "",
            amount: isFinite(amountFromOption) && amountFromOption > 0 ? amountFromOption : (isFinite(amountFromState) ? amountFromState : 0),
            sendCurrency: opt?.SendAmounts?.SendCurrency || "USD",
            productType: ProductType.SEND,
            CustomerId: "0",
            receiveAgentID: opt?.ReceiveAgentId ?? (opt as any)?.receiveAgentID ?? opt?.ReceiveAgentID ?? undefined,
          };
          console.log("Invoking getFieldsForProductRequest from Review step with:", payload);
          const gffpResponse : FieldsForProductInfo = await getFieldsForProductRequest(payload);
          const requiredFields: any[] = GffpHelper.filterRequiredAndOptionalFields(gffpResponse) as any[];
          console.log("Required fields:", requiredFields);

          // Group by FieldCategory with full field objects
          const groupedByCategory: Record<string, any[]> = requiredFields.reduce((acc: Record<string, any[]>, field: any) => {
            const category = field.FieldCategory ?? field.fieldCategory ?? 'Uncategorized';
            if (!acc[category]) acc[category] = [];
            acc[category].push(field);
            return acc;
          }, {});

          // Stable sort within each category by DisplayOrder (if present)
          Object.keys(groupedByCategory).forEach((cat) => {
            groupedByCategory[cat].sort((a, b) => {
              const ao = a.DisplayOrder ?? a.displayOrder ?? 0;
              const bo = b.DisplayOrder ?? b.displayOrder ?? 0;
              return ao - bo;
            });
          });

          const distinctCategories = Object.keys(groupedByCategory);
          console.log("Grouped by category (full fields):", groupedByCategory);
          console.log("Distinct categories:", distinctCategories);

          try { setGffpFieldsByCategory?.(groupedByCategory); } catch {}
          try { setGffpCategories?.(distinctCategories); } catch {}

          // Check if customerReceiveNumber is required; if so, branch to that step next
          try {
            const rrnField: any = (requiredFields || []).find((f: any) => {
              const tag = String(f?.xmlTag ?? f?.XmlTag ?? "").toLowerCase();
              return tag === "customerreceivenumber";
            });
            if (rrnField) {
              const label: string | undefined = rrnField?.fieldLabel ?? rrnField?.FieldLabel ?? undefined;
              setRrnFieldLabel(label);
              const rrnIndex = steps.findIndex(s => s.key === "customerReceiveNumber");
              if (rrnIndex !== -1) {
                setCurrentStep(rrnIndex);
                return;
              }
            }
          } catch {}
        } catch (err: any) {
          console.error("getFieldsForProductRequest failed:", err);
          const errMsg = err?.message || "Unable to load required fields. Please try again.";
          Alert.alert("Message", String(errMsg), [{ text: "OK" }]);
          return; // stay on review step on failure
        }

        // Success → proceed to next step
        const targetIndex = steps.findIndex(s => s.key === "senderName");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "customerReceiveNumber") {
        // After capturing RRN, go to senderName
        const targetIndex = steps.findIndex(s => s.key === "senderName");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderName") {
        const targetIndex = steps.findIndex(s => s.key === "senderAddress");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderAddress") {
        const targetIndex = steps.findIndex(s => s.key === "senderDOB");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderDOB") {
        const targetIndex = steps.findIndex(s => s.key === "senderIdentification");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderIdentification") {
        // If the selected ID type already implies a state, skip "stateOfId"
        const nextKey = selectedIdPhotoIdState ? "senderIdNumber" : "stateOfId";
        const targetIndex = steps.findIndex(s => s.key === nextKey);
        if (targetIndex !== -1) {
          try { recordStepTransition?.(nextKey); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "stateOfId") {
        const targetIndex = steps.findIndex(s => s.key === "senderIdNumber");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("senderIdNumber"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "senderIdNumber") {
        const targetIndex = steps.findIndex(s => s.key === "receiverName");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("receiverName"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "receiverName") {
        // After receiver name, check for any missing required fields from GFFP
        try {
          const gffpFieldsByCategoryLocal = useSessionStore.getState().gffpFieldsByCategory || {};
          const consumerId = consumerIdFromStore ? String(consumerIdFromStore) : "0";
          const validationReq = buildSendValidationRequestFromStore(consumerId);
          const missing = new RequiredFields().getMissingFields(gffpFieldsByCategoryLocal, validationReq);
          if (missing && missing.length > 0) {
            try { setMissingRequiredFields?.(missing as any); } catch {}
            // Route to Additional Information, then return back to Fraud Prevention after completion
            setReturnToStep("fraudPrevention");
            const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
            if (aiIndex !== -1) {
              setCurrentStep(aiIndex);
              return;
            }
          }
        } catch (e) {
          console.warn('Failed to compute missing fields after receiverName', e);
        }
        // No missing fields → proceed to Fraud Prevention
        const targetIndex = steps.findIndex(s => s.key === "fraudPrevention");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("fraudPrevention"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "fraudPrevention") {
        // Build and log update consumer profile draft before proceeding
        const targetIndex = steps.findIndex(s => s.key === "finalReview");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("finalReview"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "finalReview") {
        // If this is a new consumer, send them to the PIN step before proceeding
        if (isNewConsumer) {
          const pinIndex = steps.findIndex(s => s.key === "pin");
          if (pinIndex !== -1) {
            try { recordStepTransition?.("pin"); } catch { }
            setCurrentStep(pinIndex);
            return;
          }
        }
        else {
          // For existing consumers, perform update/validate/post flow here.
          // If validation reports missing fields, redirect to Additional Information.
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              // An error was shown; stay on current step
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              const aiIndex = steps.findIndex(s => s.key === "additionalInformation");
              if (aiIndex !== -1) {
                setCurrentStep(aiIndex);
                return;
              }
            }
          } catch (e) {
            console.warn('maultipleCalls failed', e);
          }
        }

        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("headToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      } else if (steps[currentStep].key === "additionalInformation") {
        // After entering additional info
        if (returnToStep) {
          // Path when we intentionally inserted Additional Information mid-flow (e.g., after receiverName)
          const targetIndex = steps.findIndex(s => s.key === returnToStep);
          if (targetIndex !== -1) {
            try { recordStepTransition?.(returnToStep); } catch {}
            setCurrentStep(targetIndex);
            setReturnToStep(null);
            return;
          }
        } else {
          // Path when we arrived here from finalReview due to validation requiring more fields
          try {
            const result = await maultipleCalls();
            if (result && (result as any).error) {
              // An error was shown; stay on current step
              return;
            }
            if (result && (result as any).missingFieldsFound) {
              // still missing (should be rare if validations pass)
              return;
            }
          } catch (e) {
            console.warn('maultipleCalls from additionalInformation failed', e);
            return;
          }
          const targetIndex = steps.findIndex(s => s.key === "headToCashier");
          if (targetIndex !== -1) {
            try { recordStepTransition?.("headToCashier"); } catch {}
            setCurrentStep(targetIndex);
            return;
          }
        }
      } else if (steps[currentStep].key === "pinCreated") {
        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          try { recordStepTransition?.("headToCashier"); } catch {}
          setCurrentStep(targetIndex);
          return;
        }
      }
      else if (steps[currentStep].key === "headToCashier") {
        // Finish: allow host app to handle navigation back to dashboard with kiosk token
        try { handleFinish?.(); } catch {}
        const targetIndex = steps.findIndex(s => s.key === "headToCashier");
        if (targetIndex !== -1) {
          setCurrentStep(targetIndex);
          return;
        }
      }
      // if (returnToStep !== null) {
      //   const returnToStepIndex = steps.findIndex(s => s.key === "reviewAndEditStep");
      //   console.log("returnToStep✅ Flow completed", returnToStepIndex);
      //   setCurrentStep(returnToStepIndex);        // go back to the remembered step
      //   setReturnToStep(null);               // clear so next time is normal flow
      // } else {
      //   console.log("rELEELse eturnToStep✅ Flow completed", currentStep);
      //   setCurrentStep(prev => prev + 1);    // normal forward navigation
      // }
      console.log("✅ Flow completed");
    }, 'Processing...');
  };

  const handleSkip = () => {
    // Run validation + transaction only, then jump to HeadToCashier
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    executeWithLoading(async () => {
      try {
        const result = await maultipleCalls({ skipUpdateProfile: true });
        if (result && (result as any).error) {
          // An error was shown; stay on current step
          return;
        }
      } catch (e) {
        console.warn('Skip flow failed', e);
      }
      const targetIndex = steps.findIndex(s => s.key === "headToCashier");
      if (targetIndex !== -1) {
        try { recordStepTransition?.("headToCashier"); } catch {}
        setCurrentStep(targetIndex);
      }
    }, 'Processing...');
  };

  const maultipleCalls = async (options?: { skipUpdateProfile?: boolean }) => {
    const skipUpdate = !!options?.skipUpdateProfile;
    try {
      let effectiveConsumerId: string | undefined = undefined;
      if (!skipUpdate) {
        const profile: SendMoneyPostUpdateConsumerProfileParameter = logUpdateConsumerProfileDraft();
        profile.unitProfileID = unitProfileId || "";
        profile.agentID = agentId || "";
        console.log("consumer profile===", profile);
        const profileResponse = await updateConsumerProfile(profile);
        console.log("profileResponse===", profileResponse);
        try {
          effectiveConsumerId = (profileResponse && (profileResponse.consumerId || profileResponse.ConsumerId || profileResponse.id || profileResponse.Id)) ?? undefined;
        } catch {}
      } else {
        // For skip flow, attempt to use existing consumerId from store; fall back to "0"
        try { effectiveConsumerId = consumerIdFromStore ? String(consumerIdFromStore) : "0"; } catch { effectiveConsumerId = "0"; }
      }

      try {
        const validationReq = buildSendValidationRequestFromStore(String(effectiveConsumerId ?? "0"));
        // check what are the Req, OPT fields are missing in the validationReq from requiredFields
        // get the gffpFieldsByCategory from the store
        const gffpFieldsByCategory = useSessionStore.getState().gffpFieldsByCategory || {};
        const missingRequiredFields = new RequiredFields().getMissingFields(gffpFieldsByCategory, validationReq);
        console.log("missingRequiredFields===", missingRequiredFields);
        if (missingRequiredFields && missingRequiredFields.length > 0) {
          try { setMissingRequiredFields?.(missingRequiredFields as any); } catch {}
          return { missingFieldsFound: true } as const;
        }

        console.log("sendValidationRequest draft===", validationReq);
        const validationResponse = await validateConsumer(validationReq);
        console.log("validationResponse===", validationResponse);
        // Inspect potential error envelope in validation response
        try {
          const rawData: any = (validationResponse && (validationResponse as any).data !== undefined)
            ? (validationResponse as any).data
            : validationResponse;
          const errDetail = rawData?.ErrorDetail || rawData?.errorDetail;
          if (errDetail) {
            const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? 'UNKNOWN';
            const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? 'An error occurred.';
            try { showError?.(errCode + ' ' + errString); } catch {}
            return { error: true } as const;
          }
        } catch {}
        try {
          const ref = (validationResponse && ((validationResponse.confirmationNumber) || (validationResponse.ConfirmationNumber) || (validationResponse.referenceNumber) || (validationResponse.ReferenceNumber))) ?? undefined;
          if (ref) {
            try { setConfirmationNumber?.(String(ref)); } catch {}
          }
        } catch {}
        // Build transaction parameters and post to kiosk
        try {
          const txParams = buildTransactionParametersFromStore(validationResponse, 'send');
          console.log('transactionParameters===', txParams);
          const txResp = await postTransactionToKiosk(txParams);
          console.log('postTransactionToKiosk response===', txResp);
        } catch (e) {
          console.warn('Failed to post transaction to kiosk', e);
        }
      } catch (e) {
        console.warn("Failed to build send validation request", e);
        try { showError?.(String((e as any)?.message || 'Validation failed. Please try again.')); } catch {}
        return { error: true } as const;
      }

    } catch (e) {
      console.warn("maultipleCalls failed", e);
      try { showError?.(String((e as any)?.message || 'Something went wrong. Please try again.')); } catch {}
      return { error: true } as const;
    }
  }

  // Enable Continue button for SendingAmountEntry step
  useEffect(() => {
    if (steps[currentStep].key === "sendingAmountEntry") {
      setIsStepValid(true);
    } else if (steps[currentStep].key !== "phone") {
      // Default allow for other non-phone steps unless they manage validation
      setIsStepValid(true);
    } else {
      // Phone step manages its own
      setIsStepValid(false);
    }
  }, [currentStep]);

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCustomerNotFound = () => {
    const targetIndex = steps.findIndex(s => s.key === "country");
    if (targetIndex !== -1) {
      setCurrentStep(targetIndex);
    }
  };

  const handleForgotPassword = () => {
    console.log('handleForgotPassword currentStep==>', currentStep)
    // mark as new consumer so finalReview routes to PIN
    setIsNewConsumer?.(true);
    // Close pop up
    setShowPopup(false);

    //find index from the NEW flow, not the current one
    const newFlow = BASE_STEPS_NEW;
    const countryIndex = newFlow.findIndex(s => s.key === 'country');

    // update both pieces of state together
    setSteps(newFlow);
    if (countryIndex !== -1) {
      setCurrentStep(countryIndex);
    }
  }
  

  // // Jumps directly to a step by key
  // const handleEditStep = (key: string) => {
  //   const targetIndex = steps.findIndex(s => s.key === key);
  //   if (targetIndex !== -1) {
  //     setCurrentStep(targetIndex);
  //   }
  // };

  // const handleClose = () =>{

  // }

  return (
  <SafeAreaView style={{ flex: 1, backgroundColor: "#FFF" }}>
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      keyboardVerticalOffset={Platform.OS === "ios" ? 20 : 0}
    >
      <View style={styles.container}>
        {/* --- HEADER --- */}
        <View style={styles.header}>
          {/* Top Bar */}
          <View style={styles.topBar}>
            {(steps[currentStep].key !== "phone") ? (
              <TouchableOpacity style={styles.topBarButton} onPress={handleBack}>
                <Image source={BackIcon} style={styles.IconStyle} />
              </TouchableOpacity>
              ) : (
                <Text></Text>
              )
            }
            {(steps[currentStep].key === "sendingAmountEntry") && (
              <TouchableOpacity style={styles.topBarCountry} onPress={handleBack}>
                <Text style={styles.selectedCountryText}>{selectedCountryName}</Text>
                <Image source={DownArrow} style={styles.IconStyle} />
              </TouchableOpacity>
              )
            }
            <View style={styles.topBarRight}>
              <TouchableOpacity style={styles.topBarButton}>
                <Image source={HelpIcon} style={styles.IconStyle} />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.topBarButton}
                onPress={handleFinish}
              >
                <Image
                  source={CloseIcon}
                  style={[styles.IconStyle, { marginLeft: 10 }]}
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressRow}>
            {steps.map((_, i) => (
              <View
                key={i}
                style={[
                  styles.progressSegment,
                  { backgroundColor: i <= currentStep ? "#000" : "#ccc" },
                ]}
              />
            ))}
          </View>
        </View>

        {/* --- STEP CONTENT --- */}
        <View style={styles.content}>
          {steps[currentStep].key === "phone" ? (
            <AnyStepComponent
              ref={phoneStepRef}
              // onCustomerNotFound={handleCustomerNotFound}
              onValidationChange={handleValidationChange}
            />
          ) : (steps[currentStep].key === "country" || steps[currentStep].key === "state") ? (
            <AnyStepComponent onCountrySelected={(code: string) => handleNext(code)} />
          ) : (steps[currentStep].key === "customerReceiveNumber") ? (
            <AnyStepComponent placeholderLabel={rrnFieldLabel} />
          ) :  (steps[currentStep].key === "howTheyReceive") || (steps[currentStep].key === "stateOfId") ? (
            <AnyStepComponent onCountrySelected={handleNext} />
          ) : (steps[currentStep].key === "reviewAndEditStep") || (steps[currentStep].key === "finalReview")? (
            <AnyStepComponent onEditStep={handleEditStep} />
          ) : (steps[currentStep].key === "pin")? (
            <AnyStepComponent
              handleForgotPassword={handleForgotPassword}
              attemptsLeft={attemptsLeft}
              locked={locked}
              resetSignal={resetSignal}
              />
          ) : (
            <AnyStepComponent onValidationChange={handleValidationChange}/>
          )}
        </View>
        {/* --- FIXED FOOTER --- */}
        { (steps[currentStep].key !== "country" &&
        steps[currentStep].key !== "state" &&
        steps[currentStep].key !== "howTheyReceive")&& 
        steps[currentStep].key !== "stateOfId" &&(
        <View style={styles.footer}>
          <TouchableOpacity
            style={[
              styles.button,
              { backgroundColor: isStepValid ? "#000" : "#999" },
            ]}
            onPress={() => handleNext()}
            disabled={!isStepValid}
          >
            <Text style={styles.buttonText}>
              {currentStep === steps.length - 1 ? "Finish" : "Continue"}
            </Text>
          </TouchableOpacity>
          {(steps[currentStep].key === "pin" || steps[currentStep].key === "confirmPin") && (
            <TouchableOpacity onPress={steps === BASE_STEPS_EXISTING ? handleForgotPassword : handleSkip } style={{ marginTop: 8, alignItems: "center" }}>
              <Text style={{ color: "#2563EB", textDecorationLine: "underline" }}>
                {steps === BASE_STEPS_EXISTING ? t("CONTINUE_AS_GUEST") : 'Skip'}
                {/* {isNewConsumer ? 'Skip' : 'Forgot Password'} */}
                </Text>
            </TouchableOpacity>
          )}
        </View>
        )}
              <Modal
                visible={showPopup}
                transparent
                animationType="slide"
                // onRequestClose={() => setShowPopup(false)}
                onRequestClose={() => handleForgotPassword?.()}
              >
                <View style={styles.modalOverlay}>
                  <View style={styles.modalContent}>
                    <TouchableOpacity
                      onPress={() => handleForgotPassword?.()}
                      style={{ alignItems: 'flex-end'}}
                    >
                    <Image
                      source={CloseIcon}
                      style={{width: 24, height: 24}}
                    />
                  </TouchableOpacity>
                  <View style={{ alignItems:'center'}}>
                    <Image source={AccountLockedIcon} style={{ width: 64, height: 64, marginTop: 48}}/>
                    <Text style={styles.accountLock}>
                      {t("YOUR_ACCOUNT_LOCKED")}
                    </Text>
                    <Text style={styles.accountLockInfo}>
                      {t("YOU_CAN_CONTINUE_AS_GUEST")}
                    </Text>
                    <TouchableOpacity
                      style={styles.button}
                      onPress={() => handleForgotPassword?.()}
                    >
                      <Text style={styles.buttonText}>
                      {t("CONTINUE_AS_GUEST")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  </View>
                </View>
              </Modal>
      </View>
    </KeyboardAvoidingView>
  </SafeAreaView>
);
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: "#FFF",
  },
  header: {
    // width: "100%",
    paddingHorizontal: 24
  },
  content: {
    // flex: 1,
    // width: "100%",
  },
  footer: {
    // width: "100%",
    paddingHorizontal: 24,
    paddingBottom: Platform.OS === "ios" ? 24 : 16,
    backgroundColor: "#fff",   // keeps footer consistent
  },
  button: {
    // width: "100%",             // button always full width
    backgroundColor: "#000",
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16
  },
  topBarButton: {
    padding: 1
  },
  topBarRight: {
    flexDirection: "row"
  },
  topBarCountry: {
    flexDirection: 'row',
    padding: 8,
    backgroundColor: Colors.textInputBackground,
    alignItems: 'center',
    justifyContent: 'center'
  },
  selectedCountryText: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16
  },
  IconStyle: {
    height: 24,
    width: 24
  },
  iconText: {
    fontSize: 20,
    color: "#111"
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 6
  },
  progressSegment: {
    flex: 1,
    height: 6,
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 16
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600"
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)"
  },
  modalContent: {
    backgroundColor: "white", padding: 20,
    borderTopLeftRadius: 20, borderTopRightRadius: 20,
  },
  accountLock: {
    fontSize: 24,
    fontWeight: "500",
    lineHeight: 28,
    fontFamily: 'Rubik',
    marginVertical: 26,
    color: Colors.accountLock,
  },
  accountLockInfo: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    fontFamily: 'Rubik',
    marginBottom: 48,
    color: Colors.accountLock,
  },
  // button: {
  //   backgroundColor: Colors.titleTextColor,
  //   paddingVertical: 16,
  //   borderRadius: 8,
  //   alignItems: "center",
  // },
  // buttonText: {
  //   color: Colors.white,
  //   fontSize: 16,
  //   fontWeight: "500",
  //   fontFamily: 'Rubik',
  //   lineHeight: 20,
  //   paddingHorizontal: 100
  // },
});